import { useState } from 'react';

import { formatCoins, formatCps } from '../../lib/idle/format';
import {
  BUILDINGS,
  COST_GROWTH,
  UPGRADES,
  buildingCost,
  eachRate,
  isUnlocked,
} from '../../lib/idle/gameConfig';
import { buyBuilding, buyUpgrade, useIdle } from '../../lib/idle/idleStore';
import { usePremium } from '../../lib/session';
import { ART } from '../Idle/sidebarArt';
import Coin from '../Idle/Coin';
import { Icon } from './Icons';
import Styles from './HelpersPanel.module.css';
import UpgradeIcon, { upgradeTier } from './UpgradeIcon';

const MULTS = [1, 10, 25, 100, 'max'];

// Coin upgrades only (gem upgrades live in the Gems tab).
const COIN_UPGRADES = UPGRADES.filter((u) => u.currency === 'coins');
const BUILDING_BY_ID = BUILDINGS.reduce((map, b) => ({ ...map, [b.id]: b }), {});

/** How many copies of a helper the player can afford right now (0 if not even one). */
export const maxAffordable = (b, owned, coins) => {
  if (coins < buildingCost(b, owned, 1)) return 0;
  const raw = Math.log(
    1 + (coins * (COST_GROWTH - 1)) / (b.baseCost * Math.pow(COST_GROWTH, owned)),
  );
  let n = Math.floor(raw / Math.log(COST_GROWTH));
  if (!Number.isFinite(n) || n < 1) n = 1;
  // the cost formula rounds up, so nudge the estimate to the exact answer
  while (n > 1 && buildingCost(b, owned, n) > coins) n -= 1;
  while (n < 10000 && buildingCost(b, owned, n + 1) <= coins) n += 1;
  return n;
};

const UNITS = [
  [1e12, 'trillion'],
  [1e9, 'billion'],
  [1e6, 'million'],
];

/** 3,851,000 -> "3.851 million"; smaller numbers keep their commas. */
const compact = (n) => {
  const unit = UNITS.find(([size]) => n >= size);
  if (!unit) return formatCoins(n);
  return `${(n / unit[0]).toFixed(3).replace(/\.?0+$/, '')} ${unit[1]}`;
};

const percent = (part, whole) => {
  if (!whole) return '0%';
  const value = (part / whole) * 100;
  return `${value >= 10 ? Math.round(value) : value.toFixed(1)}%`;
};

const formatDuration = (seconds) => {
  if (!Number.isFinite(seconds)) return 'never';
  if (seconds < 60) return `${Math.max(1, Math.round(seconds))} sec`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)} hr`;
  return `${(seconds / 86400).toFixed(1)} days`;
};

/* A small square in the Upgrades section. Hover or focus shows the tooltip. */
const UpgradeTile = ({ u, state, onTip, onHideTip }) => {
  const ready = state.coins >= u.cost;
  return (
    <button
      type="button"
      className={`${Styles.tile} ${Styles[`tier${upgradeTier(u.id) + 1}`]} ${
        ready ? Styles.tileReady : Styles.tileDim
      }`}
      onMouseEnter={onTip({ kind: 'upgrade', u })}
      onFocus={onTip({ kind: 'upgrade', u })}
      onMouseLeave={onHideTip}
      onBlur={onHideTip}
      onClick={() => {
        if (ready) buyUpgrade(u.id);
      }}
      aria-label={`${u.name}. Costs ${formatCoins(u.cost)} coins`}
    >
      <UpgradeIcon id={u.id} className={Styles.tileIcon} />
    </button>
  );
};

const Tooltip = ({ tip, state }) => {
  const { top, left } = tip;
  if (tip.kind === 'upgrade') {
    const u = tip.u;
    const affordable = state.coins >= u.cost;
    return (
      <div className={Styles.tip} style={{ top, left }} role="tooltip">
        <div className={Styles.tipHead}>
          <span className={Styles.tipIcon}>
            <UpgradeIcon id={u.id} className={Styles.tileIcon} />
          </span>
          <h4 className={Styles.tipName}>{u.name}</h4>
        </div>
        <p className={`${Styles.tipLine} ${affordable ? Styles.good : Styles.bad}`}>
          <Coin size={14} /> {formatCoins(u.cost)}
        </p>
        <p className={Styles.tipDesc}>{u.desc}</p>
      </div>
    );
  }

  // A helper: owned, what each makes, what the whole stack makes and its share
  // of your coins per second, and what buying the selected amount would do.
  const { b, owned, n, cost, each, locked } = tip;
  const total = each * owned;
  const added = each * n;
  const Art = ART[b.id];
  return (
    <div className={Styles.tip} style={{ top, left }} role="tooltip">
      <div className={Styles.tipHead}>
        <span className={Styles.tipIcon}>{Art && <Art className={Styles.art} />}</span>
        <h4 className={Styles.tipName}>{b.name}</h4>
        <span className={Styles.tipOwnedCount}>owned: {owned}</span>
      </div>
      <p className={Styles.tipQuote}>{b.blurb}</p>
      {locked && <p className={`${Styles.tipLine} ${Styles.bad}`}>Premium helper</p>}
      <ul className={Styles.tipList}>
        <li>
          each {b.name.toLowerCase()} makes <strong>{formatCps(each)}</strong> coins per second
        </li>
        {owned > 0 && (
          <li>
            your {owned} make <strong>{formatCps(total)}</strong> coins per second (
            <strong>{percent(total, state.cps)}</strong> of your CPS)
          </li>
        )}
        <li>
          buying {n} adds <strong>+{formatCps(added)}</strong> coins per second
          {state.cps > 0 && <> (+{percent(added, state.cps)})</>}
        </li>
        <li>
          pays for itself in <strong>{formatDuration(cost / added)}</strong>
        </li>
        <li>
          cost for {n}: <Coin size={13} />{' '}
          <strong className={state.coins >= cost ? Styles.good : Styles.bad}>
            {formatCoins(cost)}
          </strong>
        </li>
      </ul>
    </div>
  );
};

const HelpersPanel = () => {
  const state = useIdle();
  const premium = usePremium();
  const [mult, setMult] = useState(1);
  const [tip, setTip] = useState(null);

  // Upgrades you can buy now or soon: unlocked and not bought, cheapest first.
  const available = COIN_UPGRADES.filter((u) => !state.bought[u.id] && isUnlocked(u, state)).sort(
    (x, y) => x.cost - y.cost,
  );

  // Tooltips are positioned from the hovered element, but sit just outside the
  // sidebar's right edge so the scrolling list can't clip them.
  const showTip = (data) => (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const side = event.currentTarget.closest('aside');
    const left = (side ? side.getBoundingClientRect().right : rect.right) + 10;
    const top = Math.max(8, Math.min(rect.top - 6, window.innerHeight - 290));
    setTip({ ...data, top, left });
  };
  const hideTip = () => setTip(null);

  return (
    <div className={Styles.panel}>
      <h2 className={Styles.title}>Store</h2>
      <section className={Styles.upgrades} aria-label="Upgrades">
        {available.length > 0 ? (
          <div className={Styles.tiles}>
            {available.map((u) => (
              <UpgradeTile key={u.id} u={u} state={state} onTip={showTip} onHideTip={hideTip} />
            ))}
          </div>
        ) : (
          <></>
        )}
      </section>

      <div className={Styles.multRow} role="group" aria-label="Copies to buy">
        <span className={Styles.multLabel}>Buy</span>
        {MULTS.map((m) => (
          <button
            key={m}
            type="button"
            className={`${Styles.mult} ${mult === m ? Styles.multOn : ''}`}
            onClick={() => setMult(m)}
            aria-pressed={mult === m}
          >
            {m === 'max' ? 'MAX' : `×${m}`}
          </button>
        ))}
      </div>

      <ul className={Styles.list}>
        {BUILDINGS.map((b) => {
          const owned = state.owned[b.id] || 0;
          const locked = b.premium && !premium;
          const n = mult === 'max' ? Math.max(1, maxAffordable(b, owned, state.coins)) : mult;
          const cost = buildingCost(b, owned, n);
          const canAfford = state.coins >= cost;
          const each = eachRate(b, state.bought);
          const Art = ART[b.id];
          const tipData = { kind: 'helper', b, owned, n, cost, each, locked };

          return (
            <li key={b.id}>
              {/* Locked helpers stay clickable on purpose: buyBuilding opens the Premium pricing modal. */}
              <button
                type="button"
                className={`${Styles.row} ${locked ? Styles.rowLocked : ''} ${
                  !locked && !canAfford ? Styles.rowBroke : ''
                } ${!locked && canAfford ? Styles.rowReady : ''}`}
                onClick={() => buyBuilding(b.id, n)}
                onMouseEnter={showTip(tipData)}
                onFocus={showTip(tipData)}
                onMouseLeave={hideTip}
                onBlur={hideTip}
                aria-label={
                  locked
                    ? `Unlock ${b.name} with Premium`
                    : `Hire ${n} ${b.name} for ${formatCoins(cost)} coins. You have ${owned}.`
                }
              >
                <span className={Styles.artBox}>{Art && <Art className={Styles.art} />}</span>
                <span className={Styles.info}>
                  <span className={Styles.name}>{b.name}</span>
                  <span className={Styles.cost}>
                    {locked ? (
                      <>
                        <Icon name="lock" className={Styles.costLock} /> Premium
                      </>
                    ) : (
                      <>
                        {n > 1 && <em>×{n}</em>}
                        <Coin size={14} /> {compact(cost)}
                      </>
                    )}
                  </span>
                </span>
                <span className={Styles.count}>{owned > 0 ? owned : ''}</span>
              </button>
            </li>
          );
        })}
      </ul>

      {tip && <Tooltip tip={tip} state={state} />}
    </div>
  );
};

export default HelpersPanel;